'use strict'

const visualizerToastIds = {
    saving: 'savingToast',
    saved: 'savedToast',
    error: 'saveErrorToast',
    login: 'loginSuccessToast',
}

const whiteCloseVariants = new Set(['primary', 'secondary', 'success', 'danger', 'dark'])

/**
 * @param {string|HTMLElement} target
 * @returns {string|HTMLElement}
 */
function resolveToastTarget(target) {
    return visualizerToastIds[target] || target
}

/**
 * @param {string|HTMLElement} target
 * @returns {HTMLElement}
 */
function getToastElement(target) {
    const resolvedTarget = resolveToastTarget(target)
    return resolvedTarget instanceof HTMLElement ? resolvedTarget : document.getElementById(resolvedTarget)
}

/**
 * @param {string|HTMLElement} target
 * @param {Partial<{delay:number, autohide:boolean}>} [options]
 * @returns {bootstrap.Toast}
 */
function getToastInstance(target, options = {}) {
    return bootstrap.Toast.getOrCreateInstance(getToastElement(target), options)
}

/**
 * @param {HTMLElement} element
 * @param {string} closeLabel
 * @returns {HTMLElement}
 */
function createToastElement(element, closeLabel) {
    element.className = 'toast align-items-center border-0'
    element.setAttribute('role', 'alert')
    element.setAttribute('aria-live', 'assertive')
    element.setAttribute('aria-atomic', 'true')

    const wrapper = document.createElement('div')
    wrapper.className = 'd-flex'

    const body = document.createElement('div')
    body.className = 'toast-body'

    const closeButton = document.createElement('button')
    closeButton.type = 'button'
    closeButton.className = 'btn-close me-2 m-auto'
    closeButton.setAttribute('data-bs-dismiss', 'toast')
    closeButton.setAttribute('aria-label', closeLabel)

    wrapper.append(body, closeButton)
    element.appendChild(wrapper)

    return element
}

/**
 * @param {string|HTMLElement} target
 * @param {string} closeLabel
 * @returns {HTMLElement}
 */
function ensureToastElement(target, closeLabel) {
    const element = getToastElement(target)
    if (element) return { element, created: false }

    const toastElement = createToastElement(document.createElement('div'), closeLabel)
    toastElement.id = String(resolveToastTarget(target))
    document.body.appendChild(toastElement)

    return { element: toastElement, created: true }
}

/**
 * @param {string} variant
 * @returns {string}
 */
function normalizeVariant(variant) {
    return variant === 'error' ? 'danger' : variant
}

/**
 * @param {HTMLElement} element
 * @param {Object} [options={}]
 * @param {string} [options.message]
 * @param {string} [options.variant]
 * @param {string} [options.extraClass]
 * @param {string} [options.closeLabel='Close']
 * @param {boolean} [options.applyDefaults=false]
 * @returns {void}
 */
function configureToast(element, { message, variant, extraClass, closeLabel = 'Close', applyDefaults = false } = {}) {
    const normalizedVariant = variant ? normalizeVariant(variant) : applyDefaults ? 'success' : null

    if (normalizedVariant) {
        element.className = ['toast', 'align-items-center', 'border-0', `text-bg-${normalizedVariant}`, extraClass].filter(Boolean).join(' ')
    } else if (extraClass) {
        element.classList.add(...extraClass.split(/\s+/).filter(Boolean))
    }

    if (message !== undefined) {
        element.querySelector('.toast-body').textContent = message
    }

    const closeButton = element.querySelector('.btn-close')
    closeButton.setAttribute('aria-label', closeLabel)
    if (normalizedVariant) {
        closeButton.className = 'btn-close me-2 m-auto'
        closeButton.classList.toggle('btn-close-white', whiteCloseVariants.has(normalizedVariant))
    }
}

/**
 *
 * @param {string|HTMLElement} target
 * @returns {void}
 */
function hideToast(target) {
    getToastInstance(target).hide()
}

/**
 * Show one toast.
 * Visualizer aliases keep one save toast visible at a time.
 * Existing toast markup is reused, otherwise a standard toast is created.
 *
 * @param {string|HTMLElement} target
 * @param {Object} [options={}]
 * @param {string} [options.message]
 * @param {string} [options.variant]
 * @param {string} [options.extraClass]
 * @param {string} [options.closeLabel='Close']
 * @param {number} [options.delay=6000]
 * @param {boolean} [options.autohide=true]
 * @returns {bootstrap.Toast}
 */
export function showToast(target, {
    message,
    variant,
    extraClass,
    closeLabel = 'Close',
    delay = 6000,
    autohide = true,
} = {}) {
    const { element, created } = ensureToastElement(target, closeLabel)
    configureToast(element, { message, variant, extraClass, closeLabel, applyDefaults: created })

    if (visualizerToastIds[target]) {
        Object.keys(visualizerToastIds)
            .filter((toastType) => toastType !== target)
            .forEach(hideToast)
    }

    const toast = getToastInstance(element, { delay, autohide })
    toast.show()

    return toast
}

/**
 * Show every toast matching one selector.
 *
 * @param {string} selector
 * @returns {void}
 */
export function showToasts(selector) {
    document.querySelectorAll(selector).forEach((element) => {
        showToast(element)
    })
}
